export const TaxFormCode = { Form941: 1, Form940: 2 } as const;
export const taxFormCodeLabels: Record<number, string> = { 1: 'Form 941 (Quarterly)', 2: 'Form 940 (Annual)' };

/** `status` is the raw wire string ("PendingApproval"/"Accepted"/"Rejected"/"Denied") — matches this
 * project's convention of leaving status-like fields as descriptive strings rather than numeric wire
 * enums. A filing is prepared by one user (`preparedBy*`) and must be approved or denied by a DIFFERENT
 * user (`reviewedBy*`) — see docs/tax-filings.md. */
export interface TaxFilingResponse {
  id: string;
  companyId: string;
  formCode: number;
  taxYear: number;
  quarter: number | null;
  status: string;
  providerSubmissionId: string | null;
  failureReason: string | null;
  preparedByUserId: string;
  preparedByEmailSnapshot: string;
  preparedAtUtc: string;
  reviewedByUserId: string | null;
  reviewedByEmailSnapshot: string | null;
  reviewedAtUtc: string | null;
  denialReason: string | null;
  acceptedAtUtc: string | null;
  rejectedAtUtc: string | null;
}

export interface QuarterlyFormResponse {
  id: string;
  companyId: string;
  taxYear: number;
  quarter: number;
  taxLiabilityId: string;
  numberOfEmployees: number;
  totalWages: number;
  totalTaxWithheld: number;
  totalDeposits: number;
  balanceDue: number;
  taxFilingId: string | null;
  generatedByEmailSnapshot: string;
  generatedAtUtc: string;
}

export interface AnnualFormResponse {
  id: string;
  companyId: string;
  taxYear: number;
  taxLiabilityId: string;
  numberOfEmployees: number;
  totalWages: number;
  totalTaxWithheld: number;
  totalDeposits: number;
  balanceDue: number;
  taxFilingId: string | null;
  generatedByEmailSnapshot: string;
  generatedAtUtc: string;
}

/** EPN-41 — one card per real, backed filing period (Form941 Q1-Q4 + Form940), for a given tax year.
 * `status` is computed server-side on every read, never persisted. */
export type TaxFilingDashboardStatus = 'NotPrepared' | 'InReview' | 'Filed' | 'NeedsAttention';

export const taxFilingDashboardStatusLabels: Record<TaxFilingDashboardStatus, string> = {
  NotPrepared: 'Not Prepared',
  InReview: 'In Review',
  Filed: 'Filed',
  NeedsAttention: 'Needs Attention',
};

export interface TaxFilingDashboardCardResponse {
  companyId: string;
  formCode: number;
  taxYear: number;
  quarter: number | null;
  dueDate: string;
  status: TaxFilingDashboardStatus;
  taxFilingId: string | null;
  providerSubmissionId: string | null;
  failureReason: string | null;
  preparedByEmailSnapshot: string | null;
  preparedAtUtc: string | null;
  reviewedByEmailSnapshot: string | null;
  reviewedAtUtc: string | null;
  denialReason: string | null;
}

/** EPN-40 — one row per company with at least one filing for the tax year, returned only to a
 * platform.taxfilings.read holder (SuperAdmin). A company absent from this list has never prepared a
 * filing this year — the caller combines this with the full company roster (from the Super Admin
 * Dashboard's own company list, EPN-44) and treats any missing company as all 5 periods Not Prepared. */
export interface TaxFilingPlatformSummaryResponse {
  companyId: string;
  taxYear: number;
  notPreparedCount: number;
  inReviewCount: number;
  filedCount: number;
  needsAttentionCount: number;
}

/** The five form types shown in the approved design with no real backend support at all today (no entity,
 * no calculation source, no mock-provider awareness) — see docs/epn-41-e-filing-dashboard-gaps.md. Rendered
 * as honest static "not available" cards, entirely on the frontend; never fetched from any endpoint. */
export const UNAVAILABLE_ANNUAL_FORM_CARDS: { code: string; name: string; description: string }[] = [
  { code: '944', name: '944 Form', description: "Employer's Annual Federal Tax Return" },
  { code: '8655', name: '8655 Form', description: 'Reporting Agent Authorization Form' },
  { code: 'W2', name: 'W2 Form', description: 'Wage and Tax Statement' },
  { code: 'W2-C', name: 'W2-C Form', description: 'Corrected Wage and Tax Statement' },
  { code: 'W3', name: 'W3 Form', description: 'Transmittal of Wage and Tax Statements' },
];
